import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Col, Card, CardBody, Row } from 'reactstrap';
import FullCalendar from '@fullcalendar/react';
import itLocale from '@fullcalendar/core/locales/it';
import { useNavigate } from "react-router-dom";
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import Swal from 'sweetalert2';
import { get } from '../../../hook/request';
import { H4 } from '../../../AbstractElements';
import { CalendarAttivitaApi } from '../../../Types/Calendar.type'
import { ProjectEventDetails } from './ProjectEventDetails';

function isAssegnataDaMe(attivita: CalendarAttivitaApi): boolean {
  const raw =
    (attivita as any).assegnata_da_me ??
    (attivita as any).assegnato_da_me ??
    (attivita as any).assegnati_da_me;
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return raw === true || raw === 1;
}

function mapAttivitaToEvents(attivita: CalendarAttivitaApi[]): EventInput[] {
  const grouped: Record<
    string,
    { progettoID: number; projectName: string; day: string; stati: number[] }
  > = {};

  attivita.forEach((a) => {
    const rawDate = a.data_svolgere || a.data_esecuzione;
    if (!rawDate) return;

    const giorno = typeof rawDate === 'string' ? rawDate.split(' ')[0] : '';
    if (!giorno) return;

    const key = `${a.progettoID}__${giorno}`;

    if (!grouped[key]) {
      grouped[key] = {
        progettoID: Number(a.progettoID),
        projectName: a.nome,
        day: giorno,
        stati: [],
      };
    }

    grouped[key].stati.push(Number(a.stato));
  });

  return Object.values(grouped).map(group => {
    const stati = group.stati;
    let statoGruppo: number | undefined = undefined;

    if (stati.includes(682)) statoGruppo = 682; // Da fare
    else if (stati.includes(670)) statoGruppo = 670; // In corso
    else if (stati.includes(671)) statoGruppo = 671; // Completato

    let backgroundColor = '#90CAF9';
    switch (statoGruppo) {
      case 670: backgroundColor = 'var(--status-in-corso)'; break;
      case 682: backgroundColor = 'var(--status-da-fare)'; break;
      case 671: backgroundColor = 'var(--status-completato)'; break;
    }

    return {
      id: `${group.progettoID}__${group.day}`,
      title: group.projectName,
      start: group.day,
      allDay: true,
      backgroundColor,
      borderColor: backgroundColor,
      extendedProps: {
        progettoID: group.progettoID,
        day: group.day,
        stato: statoGruppo,
      },
    } as EventInput;
  });
}





export default function DefaultCalendar() {
  const [events, setEvents] = useState<EventInput[]>([]);
  const [assegnateDaMeEvents, setAssegnateDaMeEvents] = useState<EventInput[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [attivitaByProjectDay, setAttivitaByProjectDay] = useState<
    Record<string, CalendarAttivitaApi[]>
  >({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response: any = await get('progetti/calendar');
        const raw: any = response.data ?? response; 

        const allActivities: CalendarAttivitaApi[] = [];
        const assegnateDaMeActivities: CalendarAttivitaApi[] = [];
        const byProjectDay: Record<string, CalendarAttivitaApi[]> = {};
        const byProjectDayAssegnateDaMe: Record<string, CalendarAttivitaApi[]> = {};

        Object.values(raw).forEach((projectBlock: any) => {
          Object.values(projectBlock).forEach((a: any) => {
            const att = a as CalendarAttivitaApi;
            const isMine = isAssegnataDaMe(att);
            if (isMine) assegnateDaMeActivities.push(att);
            else allActivities.push(att);

            const rawDate = att.data_svolgere || att.data_esecuzione;
            const day = typeof rawDate === 'string'
              ? rawDate.split(' ')[0]
              : '';

            if (day) {
              const key = `${att.progettoID}__${day}`;
              if (isMine) {
                if (!byProjectDayAssegnateDaMe[key]) byProjectDayAssegnateDaMe[key] = [];
                byProjectDayAssegnateDaMe[key].push(att);
              } else {
                if (!byProjectDay[key]) byProjectDay[key] = [];
                byProjectDay[key].push(att);
              }
            }
          });
        });

        const mappedEvents = mapAttivitaToEvents(allActivities);
        const mappedEventsWithFlag = mappedEvents.map((event) => ({
          ...event,
          extendedProps: {
            ...(event.extendedProps as any),
            assegnataDaMe: false,
          },
        }));
        const mappedAssegnateDaMeEvents = mapAttivitaToEvents(assegnateDaMeActivities).map((event) => ({
          ...event,
          id: `mine__${String(event.id)}`,
          backgroundColor: '#0d6efd',
          borderColor: '#0d6efd',
          extendedProps: {
            ...(event.extendedProps as any),
            assegnataDaMe: true,
          },
        }));
        setEvents(mappedEventsWithFlag);
        setAssegnateDaMeEvents(mappedAssegnateDaMeEvents);
        const mergedByProjectDay: Record<string, CalendarAttivitaApi[]> = { ...byProjectDay };
        Object.entries(byProjectDayAssegnateDaMe).forEach(([key, items]) => {
          mergedByProjectDay[key] = [...(mergedByProjectDay[key] ?? []), ...items];
        });
        setAttivitaByProjectDay(mergedByProjectDay);
      } catch (err: any) {
        console.error('Errore nel caricamento del calendario:', err);
        setError('Errore nel caricamento delle attività');
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarData();
  }, []);


  const openProjectDetails = (
    progettoID: number,
    day: string,
    projectName: string,
    assegnataDaMe: boolean
  ) => {
    const key = `${progettoID}__${day}`;
    const projectActivities = (attivitaByProjectDay[key] ?? []).filter(
      (attivita) => isAssegnataDaMe(attivita) === assegnataDaMe
    );

    const eventDetailsHtml = ReactDOMServer.renderToString(
      <ProjectEventDetails
        projectName={projectName}
        activities={projectActivities}
      />
    );

    Swal.fire({
      title: projectName,
      html: eventDetailsHtml,
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Vai al progetto',
      cancelButtonText: 'Chiudi',
      confirmButtonColor: '#3085d6',
    }).then((result) => {
      if (result.isConfirmed) {
        const url = `/progetti/${progettoID}`;
        navigate(url);
      }
    });
  };

  const eventClick = (arg: EventClickArg) => {
    const { progettoID, day, assegnataDaMe } = arg.event.extendedProps as any;
    openProjectDetails(
      Number(progettoID),
      String(day),
      arg.event.title,
      Boolean(assegnataDaMe)
    );
  };




  return (
    <div className="page-body">
      <Row>
        <Col xl={12} className='calendar-basic px-0'>
          <Card>
            <CardBody>
              <Row>
                <Col xl={2}>
                  <div className="md-sidebar mb-3 box-col-12 md-sidebar mb-3">
                    <div id='external-events' className="md-sidebar-aside job-left-aside custom-scrollbar">
                      <H4>AGENDA</H4>
                      <div>
                        <div className="calendar-legend">
                          <div className="calendar-legend-item">
                            <span
                              className="calendar-legend-color"
                              style={{ backgroundColor: 'var(--status-da-fare)' }}
                            ></span>
                            <span>Da fare</span>
                          </div>

                          <div className="calendar-legend-item">
                            <span
                              className="calendar-legend-color"
                              style={{ backgroundColor: 'var(--status-in-corso)' }}
                            ></span>
                            <span>In corso</span>
                          </div>

                          <div className="calendar-legend-item">
                            <span
                              className="calendar-legend-color"
                              style={{ backgroundColor: 'var(--status-completato)' }}
                            ></span>
                            <span>Completato</span>
                          </div>

                          <div className="calendar-legend-item">
                            <span
                              className="calendar-legend-color"
                              style={{ backgroundColor: '#0d6efd' }}
                            ></span>
                            <span>Creato da me</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>


                <Col xl={10}>
                  {loading && <p>Caricamento calendario...</p>}
                  {error && <p className="text-danger">{error}</p>}

                  <div className="calendar-default">
                    <FullCalendar
                     locale={itLocale}
                      initialView="dayGridMonth"
                        buttonText={{
                          today: 'Oggi',
                          month: 'Mese',
                          week: 'Settimana',
                          day: 'Giorno',
                      }}
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                      }}
                      height="85vh"
                      rerenderDelay={10}
                      eventDurationEditable={false}
                      editable={true}
                      droppable={true}
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      events={[...events, ...assegnateDaMeEvents]}
                      eventClick={eventClick}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );

}
